import {
  cloneElement,
  isValidElement,
  useState,
  type ErrorInfo,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Flex } from 'antd';
import styled, { css } from 'styled-components';
import {
  SplitPanel,
  SplitPanelItem,
  SplitResizeHandle,
} from '../../SplitPanel';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import { EASING, ExpandingOverlay, GROW_MS } from './ExpandingOverlay';
import { SlotErrorBoundary } from '../SlotErrorBoundary';
import { ExperienceCanvas } from '../ExperienceCanvas';
import { colors, radii, shadows, spacing } from '../../../theme-v2';

export type SidebarPosition = 'left' | 'right' | 'none';

/**
 * Vertical space between the two sidebar cards, and so also the height of
 * the resize handle that occupies it (see `StyledSidebarResizeHandle`).
 */
const SIDEBAR_GAP = spacing.xl;

/**
 * `react-resizable-panels`' Panel renders as `display: block`. Adding
 * this style turns each Panel into a flex column container so wrapped
 * panels (CollapsibleSection with `flex: 1 1 auto`) grow to fill it.
 *
 * `minWidth: 0` + `overflow: hidden` keep wide content (e.g. the PDF
 * viewer) clipped to the panel's allocated width so it can't spill over
 * the resize handle into the neighbouring panel at narrow sizes.
 */
const flexColumnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',
};

/**
 * The sidebar's Panel, unlike the document's, must NOT clip: its surface
 * casts a shadow that deliberately spills sideways over the document.
 * Panel ships `overflow: hidden` by default, which would trim that shadow
 * away before `StyledSidebarSurface`'s `clip-path` ever gets a say — the
 * library merges `{...libraryDefaults, ...styleFromProps}`, so overriding
 * it here wins. Content stays clipped by the `clip-path` on the surface.
 */
const sidebarPanelStyle: React.CSSProperties = {
  minWidth: 0,
  overflow: 'visible',
};

/**
 * Sizing for one half of the sidebar split. Longhands, not the `flex`
 * shorthand: they replace the library's own keys by name (it merges
 * `{...libraryDefaults, ...styleFromProps}`).
 */
const panelSizeStyle = (
  bothOpen: boolean,
  open: boolean,
): React.CSSProperties => {
  // Sizes are the library's while both are open, so dragging still works.
  if (bothOpen) return flexColumnStyle;
  return {
    ...flexColumnStyle,
    ...(open
      ? { flexGrow: 1, flexShrink: 1, flexBasis: 0 } // fills the rest
      : { flexGrow: 0, flexShrink: 0, flexBasis: 'auto' }), // header strip only
  };
};

/** Shape of the collapse props the layout injects into sidebar panel slots. */
interface OpenAwareProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  collapsible?: boolean;
}

export interface ExperiencePageLayoutProps {
  /**
   * Where the Knowledge Hub + Chatbot sidebar sits relative to the
   * document. `none` renders just the document fullscreen (no sidebar).
   * Defaults to `right`.
   */
  sidebarPosition?: SidebarPosition;
  /** Document viewer / paper slot (e.g. PDF, HTML, markdown). */
  documentArea: ReactNode;
  /**
   * Top sidebar half — typically `<KnowledgeHubPanel />`. The layout
   * injects `open` + `onOpenChange` props into this element so its
   * collapse state is coordinated with the chatbot panel.
   */
  knowledgeHub?: ReactNode;
  /**
   * Bottom sidebar half — typically `<ChatbotPanel />`. Same
   * coordination as `knowledgeHub`.
   */
  chatbot?: ReactNode;
  /** Optional top header bar — typically `<ExperiencePageHeader />`. */
  header?: ReactNode;
  hideHeader?: boolean;
  /**
   * When provided, takes over the entire sidebar region (replacing the
   * Knowledge Hub + Chatbot split) while the document pane stays in
   * place. Used by the expand-on-play UX: clicking play in the Video tab
   * expands the full chaptered player over the sidebar; closing it clears
   * this prop and the tabs return.
   */
  sidebarOverlay?: ReactNode;
  /**
   * On-screen rect of the element the overlay should grow out of /
   * shrink back into (e.g. the Video tab's poster thumbnail). When set,
   * the overlay animates between this rect and its full size (FLIP);
   * otherwise it fades.
   */
  overlayOrigin?: DOMRect | null;
  /**
   * Drives the overlay enter/exit animation. `true` (default) grows it
   * in; flip to `false` to play the shrink-back-out animation while
   * keeping `sidebarOverlay` mounted, then clear `sidebarOverlay` in
   * `onOverlayExited`.
   */
  overlayOpen?: boolean;
  /** Fires once the overlay's exit animation finishes. */
  onOverlayExited?: () => void;
  /**
   * Controlled open state of the Knowledge Hub panel. When provided,
   * overrides the layout's internal state so a page can collapse the Hub
   * to its header strip (e.g. to enlarge the chat on message send).
   */
  knowledgeHubOpen?: boolean;
  /** Fired when the Knowledge Hub open state should change (controlled mode). */
  onKnowledgeHubOpenChange?: (open: boolean) => void;
  /**
   * Controlled open state of the Chatbot panel — same contract as
   * `knowledgeHubOpen`. Lets a page collapse the chat (handing its space to
   * the Knowledge Hub) for something other than the KH's own toggle — e.g.
   * Podcast play expanding the Knowledge Hub to full width (MED-7075).
   */
  chatbotOpen?: boolean;
  /** Fired when the Chatbot open state should change (controlled mode). */
  onChatbotOpenChange?: (open: boolean) => void;
  /**
   * Document vs sidebar split — initial size of the document pane as a
   * percentage. Defaults to 65 (so sidebar is ~35%).
   */
  defaultDocumentSize?: number;
  /**
   * Knowledge Hub vs Chatbot split inside the sidebar — initial size of
   * the Knowledge Hub pane as a percentage. Defaults to 55.
   */
  defaultKnowledgeHubSize?: number;
  /**
   * Whether the document ↔ sidebar split can be dragged. Off fixes the panes
   * at `defaultDocumentSize` with nothing between them. The Knowledge Hub ↔
   * Chatbot split stays resizable either way.
   */
  resizableDocumentSplit?: boolean;
  /**
   * Called when a slot's subtree throws. Each slot is wrapped in its own
   * `SlotErrorBoundary`, so a crash costs that panel and not the page;
   * this is how the crash still reaches the app's error reporter.
   */
  onSlotError?: (error: Error, errorInfo: ErrorInfo) => void;
  className?: string;
}

/**
 * ExperiencePageLayout — the shell of the Knowledge Hub experience
 * page. Composes:
 *
 *   ┌─ header ───────────────────────────────────────────────────────┐
 *   ├──────────────┬─────────────────────────────────────────────────┤
 *   │              │ Knowledge Hub                                   │
 *   │  Document    ├─────────────────────────────────────────────────┤
 *   │              │ Chatbot                                         │
 *   └──────────────┴─────────────────────────────────────────────────┘
 *
 * - `sidebarPosition='left'`: sidebar on the left, document on the right
 * - `sidebarPosition='right'` (default): document on the left, sidebar on the right
 * - `sidebarPosition='none'`: just the document, fullscreen
 *
 * Sidebar collapse coordination: the layout owns the open state of the
 * two sidebar panels. When both are open, they share a resizable split.
 * When one is collapsed, it shrinks to its header strip and the other
 * fills the remaining space. A panel rendered on its own offers no
 * collapse toggle: there is nothing to hand its space to.
 */
export const ExperiencePageLayout = ({
  sidebarPosition = 'right',
  documentArea,
  knowledgeHub,
  chatbot,
  header,
  hideHeader,
  sidebarOverlay,
  overlayOrigin,
  overlayOpen = true,
  onOverlayExited,
  knowledgeHubOpen,
  onKnowledgeHubOpenChange,
  chatbotOpen,
  onChatbotOpenChange,
  defaultDocumentSize = 65,
  defaultKnowledgeHubSize = 55,
  resizableDocumentSplit = true,
  onSlotError,
  className,
}: ExperiencePageLayoutProps) => {
  const [internalKhOpen, setInternalKhOpen] = useState(true);
  const [internalChatbotOpen, setInternalChatbotOpen] = useState(true);
  /*
   * A crashed sidebar panel is force-expanded. Its collapse header lives
   * inside the panel component, which the error fallback replaces — so a
   * panel that crashes while collapsed would otherwise be stuck as a
   * header-height strip with no control left that could expand it.
   */
  const [khCrashed, setKhCrashed] = useState(false);
  const [chatbotCrashed, setChatbotCrashed] = useState(false);

  // Controlled when `knowledgeHubOpen` is passed, else layout-owned.
  const isKhControlled = knowledgeHubOpen !== undefined;
  const khOpen = isKhControlled ? knowledgeHubOpen : internalKhOpen;

  const setKhOpen = (next: boolean) => {
    if (isKhControlled) onKnowledgeHubOpenChange?.(next);
    else setInternalKhOpen(next);
  };

  // Same controlled/uncontrolled split as the Knowledge Hub above.
  const isChatbotControlled = chatbotOpen !== undefined;
  const chatbotOpenResolved = isChatbotControlled
    ? chatbotOpen
    : internalChatbotOpen;

  const setChatbotOpen = (next: boolean) => {
    if (isChatbotControlled) onChatbotOpenChange?.(next);
    else setInternalChatbotOpen(next);
  };

  /*
   * The sidebar always keeps one expanded panel. Collapsing the only
   * expanded one therefore swaps: it hands its space to the other panel
   * rather than doing nothing. With the other slot absent there is nothing
   * to swap to, so the panel is rendered without a toggle (`collapsible`
   * below) and these guards back that up. The slot checks are NOT redundant
   * next to the open flags: an absent panel's open state can hold any value
   * (a controlling consumer may start it `false`), so it says nothing about
   * whether the panel exists.
   */
  const handleKhToggle = (next: boolean) => {
    if (!next && !chatbot) return;
    setKhOpen(next);
    if (!next && !chatbotOpenResolved) setChatbotOpen(true);
    // Expanding the Hub hands the sidebar to it: the chat collapses to its
    // header strip (still mounted, so its history and draft are kept).
    if (next && chatbot) setChatbotOpen(false);
  };

  // Lets the chatbot's re-expansion put the Hub/Chatbot split back to its
  // default without remounting either panel.
  const [khPanel, setKhPanel] = useState<ImperativePanelHandle | null>(null);

  const handleChatbotToggle = (next: boolean) => {
    if (!next && !knowledgeHub) return;
    setChatbotOpen(next);
    if (!next && !khOpen) setKhOpen(true);
    if (next) khPanel?.resize(defaultKnowledgeHubSize);
  };

  const khEl = injectOpenState(
    knowledgeHub,
    khOpen,
    handleKhToggle,
    Boolean(chatbot),
  );
  const chatbotEl = injectOpenState(
    chatbot,
    chatbotOpenResolved,
    handleChatbotToggle,
    Boolean(knowledgeHub),
  );

  /*
   * Each slot gets its own error boundary, so one panel throwing leaves
   * the others usable instead of dropping the reader on a full-page
   * error screen. Wrapped here rather than in the branches below so the
   * `khEl && chatbotEl` slot-presence checks keep seeing `null` for an
   * absent slot — a boundary around nothing would read as "present".
   */
  const khSlot = withSlotBoundary(khEl, {
    onError: (error, errorInfo) => {
      setKhCrashed(true);
      onSlotError?.(error, errorInfo);
    },
    onRetry: () => setKhCrashed(false),
  });
  const chatbotSlot = withSlotBoundary(chatbotEl, {
    onError: (error, errorInfo) => {
      setChatbotCrashed(true);
      onSlotError?.(error, errorInfo);
    },
    onRetry: () => setChatbotCrashed(false),
  });
  /*
   * A crashed overlay would otherwise trap the reader: it still covers the
   * whole sidebar, `headerHidden` below still hides the page header, and
   * the only close control (`onClose` on the player) died with the
   * subtree. Dismissing it puts them back on a working page, which beats
   * an error card pinned over a dead overlay.
   */
  const overlaySlot = withSlotBoundary(sidebarOverlay, {
    onError: (error, errorInfo) => {
      onSlotError?.(error, errorInfo);
      onOverlayExited?.();
    },
  });

  // A lone panel has no toggle, so it's always shown whatever its open state.
  const khShown = khOpen || khCrashed || !chatbotEl;
  const chatbotShown = chatbotOpenResolved || chatbotCrashed || !khEl;
  const bothOpen = khShown && chatbotShown;
  const hasAnySidebar = Boolean(khEl || chatbotEl || sidebarOverlay);

  const headerHidden = hideHeader ?? (Boolean(sidebarOverlay) && overlayOpen);

  let sidebar: ReactNode = null;
  if (sidebarOverlay) {
    // Expand-on-play: the overlay (full chaptered player) fills the
    // entire sidebar region, growing out of the thumbnail. The KH/Chatbot
    // split is hidden underneath.
    sidebar = (
      <ExpandingOverlay
        origin={overlayOrigin}
        open={overlayOpen}
        onExitComplete={onOverlayExited}
      >
        {overlaySlot}
      </ExpandingOverlay>
    );
    // Branch on slot presence, never on open state: swapping containers
    // would remount both panels and wipe their state (MED-6963). A lone
    // panel goes to the flex branch, which stretches it to full height.
  } else if (khEl && chatbotEl) {
    sidebar = (
      <SplitPanel direction="vertical">
        <SplitPanelItem
          ref={setKhPanel}
          defaultSize={defaultKnowledgeHubSize}
          minSize={20}
          style={panelSizeStyle(bothOpen, khShown)}
        >
          <StyledSlotCard $expanded={khShown}>{khSlot}</StyledSlotCard>
        </SplitPanelItem>
        {/* Kept mounted: removing it reparents — and so remounts — the
            panel after it. */}
        <StyledSidebarResizeHandle disabled={!bothOpen} $idle={!bothOpen} />
        <SplitPanelItem
          defaultSize={100 - defaultKnowledgeHubSize}
          minSize={20}
          style={panelSizeStyle(bothOpen, chatbotShown)}
        >
          <StyledSlotCard $expanded={chatbotShown}>
            {chatbotSlot}
          </StyledSlotCard>
        </SplitPanelItem>
      </SplitPanel>
    );
  } else if (hasAnySidebar) {
    sidebar = (
      <StyledFlexSidebar>
        {khEl && <StyledSlotCard $expanded={khShown}>{khSlot}</StyledSlotCard>}
        {chatbotEl && (
          <StyledSlotCard $expanded={chatbotShown}>
            {chatbotSlot}
          </StyledSlotCard>
        )}
      </StyledFlexSidebar>
    );
  }

  // The expanded video fills the sidebar edge to edge. Stays flush for the
  // exit animation too, so it doesn't jump while shrinking back.
  const sidebarSurface = sidebar ? (
    <StyledSidebarSurface
      $position={sidebarPosition}
      $flush={Boolean(sidebarOverlay)}
    >
      {sidebar}
    </StyledSidebarSurface>
  ) : null;

  const sidebarSize = 100 - defaultDocumentSize;

  const documentPane = (
    <StyledDocumentSlot>
      <SlotErrorBoundary onError={onSlotError}>
        {documentArea}
      </SlotErrorBoundary>
    </StyledDocumentSlot>
  );

  // Document + sidebar arrangement. Hoisted out of the JSX so the
  // no-sidebar / resizable / fixed choice reads as a branch instead of
  // a nested ternary, matching how `sidebar` is built above.
  let mainContent: ReactNode;
  if (sidebarPosition === 'none' || !sidebar) {
    mainContent = documentPane;
  } else if (resizableDocumentSplit) {
    mainContent = (
      <SplitPanel direction="horizontal">
        {sidebarPosition === 'left' && (
          <>
            <SplitPanelItem
              defaultSize={sidebarSize}
              minSize={20}
              style={sidebarPanelStyle}
            >
              {sidebarSurface}
            </SplitPanelItem>
            <SplitResizeHandle />
            <SplitPanelItem
              defaultSize={defaultDocumentSize}
              minSize={30}
              style={flexColumnStyle}
            >
              {documentPane}
            </SplitPanelItem>
          </>
        )}
        {sidebarPosition === 'right' && (
          <>
            <SplitPanelItem
              defaultSize={defaultDocumentSize}
              minSize={30}
              style={flexColumnStyle}
            >
              {documentPane}
            </SplitPanelItem>
            <SplitResizeHandle />
            <SplitPanelItem
              defaultSize={sidebarSize}
              minSize={20}
              style={sidebarPanelStyle}
            >
              {sidebarSurface}
            </SplitPanelItem>
          </>
        )}
      </SplitPanel>
    );
  } else {
    mainContent = (
      /* No `SplitPanel`: a handle-less PanelGroup warns, and fixed panes
           need none of its machinery. */
      <StyledFixedSplit>
        {sidebarPosition === 'left' && (
          <>
            <StyledFixedSidebarPane $size={sidebarSize}>
              {sidebarSurface}
            </StyledFixedSidebarPane>
            <StyledFixedDocumentPane $size={defaultDocumentSize}>
              {documentPane}
            </StyledFixedDocumentPane>
          </>
        )}
        {sidebarPosition === 'right' && (
          <>
            <StyledFixedDocumentPane $size={defaultDocumentSize}>
              {documentPane}
            </StyledFixedDocumentPane>
            <StyledFixedSidebarPane $size={sidebarSize}>
              {sidebarSurface}
            </StyledFixedSidebarPane>
          </>
        )}
      </StyledFixedSplit>
    );
  }

  return (
    <ExperienceCanvas className={className}>
      {header && (
        <StyledHeaderSlot
          $hidden={headerHidden}
          aria-hidden={headerHidden || undefined}
        >
          <StyledHeaderInner $hidden={headerHidden}>{header}</StyledHeaderInner>
        </StyledHeaderSlot>
      )}
      <StyledMain>{mainContent}</StyledMain>
    </ExperienceCanvas>
  );
};

/**
 * Clone the slot element with the layout-managed collapse props. Returns
 * null for non-element children (strings, fragments without props, …).
 * Apps typically pass `<KnowledgeHubPanel />` or `<ChatbotPanel />`, both
 * of which accept these props.
 */
const injectOpenState = (
  slot: ReactNode,
  open: boolean,
  onOpenChange: (open: boolean) => void,
  collapsible: boolean,
): ReactElement | null => {
  if (!slot || !isValidElement(slot)) return null;
  return cloneElement(slot as ReactElement<OpenAwareProps>, {
    open,
    onOpenChange,
    collapsible,
  });
};

/**
 * Put a slot behind its own error boundary, preserving `null` for an
 * absent slot so the slot-presence checks upstream stay meaningful.
 */
const withSlotBoundary = (
  slot: ReactNode,
  handlers: {
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    onRetry?: () => void;
  },
): ReactNode =>
  slot ? (
    <SlotErrorBoundary onError={handlers.onError} onRetry={handlers.onRetry}>
      {slot}
    </SlotErrorBoundary>
  ) : (
    slot
  );

const StyledHeaderSlot = styled.div<{ $hidden: boolean }>`
  flex-shrink: 0;
  display: grid;
  grid-template-rows: ${({ $hidden }) => ($hidden ? '0fr' : '1fr')};
  transition: grid-template-rows ${GROW_MS}ms ${EASING};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const StyledHeaderInner = styled.div<{ $hidden: boolean }>`
  min-height: 0;
  overflow: hidden;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  transition: visibility 0s
    ${({ $hidden }) => ($hidden ? `${GROW_MS}ms` : '0s')};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const StyledMain = styled.div`
  flex: 1;
  min-height: 0;
`;

/** Stand-in for the `SplitPanel` row when the split isn't draggable. */
const StyledFixedSplit = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
`;

/** Fixed-share counterpart of the document `SplitPanelItem`. */
const StyledFixedDocumentPane = styled.div<{ $size: number }>`
  flex: 0 0 ${({ $size }) => $size}%;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
`;

/**
 * Fixed-share counterpart of the sidebar `SplitPanelItem`. Unclipped for the
 * same reason (see `sidebarPanelStyle`): its shadow spills over the document.
 */
const StyledFixedSidebarPane = styled.div<{ $size: number }>`
  flex: 0 0 ${({ $size }) => $size}%;
  min-width: 0;
  overflow: visible;
`;

/** Document gutter. Same value as the sidebar's, so the two line up. */
const StyledDocumentSlot = styled(Flex).attrs({ vertical: true })`
  height: 100%;
  width: 100%;
  min-height: 0;
  box-sizing: border-box;
  padding: ${spacing.md}px;
  gap: ${spacing.md}px;

  /* Let the paper fill the gutter box; anything above it (e.g. the document
     switcher) keeps its natural height. */
  > :last-child {
    flex: 1;
    min-height: 0;
  }
`;

/**
 * Raised surface the sidebar panels sit on. `clip-path` replaces
 * `overflow: hidden`: it clips the content but extends past the shadow's
 * side (-40px > the 24px blur) to let it show.
 *
 * The surface always fills its Panel — no `max-width`. A cap here would
 * decouple the card from the resize handle that's supposed to be moving
 * it: past the cap the Panel keeps widening as you drag while the card
 * stays put, leaving a band of bare canvas between handle and card. The
 * sidebar's width is the Panel's business (bounded by the split's
 * `minSize`s), not the surface's.
 */
const StyledSidebarSurface = styled(Flex).attrs({ vertical: true })<{
  $position: SidebarPosition;
  $flush: boolean;
}>`
  height: 100%;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  padding: ${({ $flush }) => ($flush ? 0 : `${spacing.md}px`)};
  position: relative;
  z-index: 2;

  /* The video player rounds itself off this variable (40px in
     experience-viewer's theme). Filling the region edge to edge, it
     shouldn't have corners at all. */
  ${({ $flush }) =>
    $flush &&
    css`
      --theme-video-border-radius: 0px;
    `}

  ${({ $position }) =>
    $position === 'left'
      ? css`
          box-shadow: 4px 0 ${shadows.sidebarBlur} ${shadows.sidebarColor};
          clip-path: inset(0 -40px 0 0);
        `
      : css`
          box-shadow: -4px 0 ${shadows.sidebarBlur} ${shadows.sidebarColor};
          clip-path: inset(0 0 0 -40px);
        `}
`;

/** White card each sidebar panel sits in, floating on the canvas. */
const StyledPanelCard = styled(Flex).attrs({ vertical: true })`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: ${radii.box}px;
  background: ${colors.surface.base};
`;

/**
 * The whole gap between the cards is the drag target, marked by a short
 * always-visible grip rather than `SplitResizeHandle`'s full-width hairline.
 * The attribute selector matches the specificity of that component's rule.
 *
 * The grip is a control, so it owes WCAG 1.4.11's 3:1 against the canvas it
 * sits on (`backgrounds.canvasBase`) — that rules out the greys the rest of
 * the panel chrome uses (`text.quaternary` is 1.3:1, `text.tertiary` 2.2:1)
 * and the hover teal `accent.base` (2.2:1). Hence `text.secondary` (4.3:1)
 * resting and the darker `accent.active` (3.3:1) on hover/drag.
 */
const StyledSidebarResizeHandle = styled(SplitResizeHandle)<{
  $idle: boolean;
}>`
  &[data-panel-group-direction='vertical'] {
    /* Same height when idle: it is also the gap between the two cards. */
    height: ${SIDEBAR_GAP}px;

    ${({ $idle }) =>
      $idle &&
      css`
        cursor: default;
      `}

    > span {
      width: 28px;
      height: 3px;
      border-radius: 2px;
      background: ${colors.text.secondary};
      ${({ $idle }) =>
        $idle &&
        css`
          display: none;
        `}
    }

    &:hover > span,
    &[data-resize-handle-active] > span {
      background: ${colors.accent.active};
    }
  }
`;

/** Single-panel sidebar. `SIDEBAR_GAP` matches the split branch's handle. */
const StyledFlexSidebar = styled(Flex).attrs({ vertical: true })`
  height: 100%;
  min-height: 0;
  gap: ${SIDEBAR_GAP}px;
`;

/* A card that also carries the slot's expand/collapse sizing. */
const StyledSlotCard = styled(StyledPanelCard)<{ $expanded: boolean }>`
  ${({ $expanded }) =>
    $expanded ? `flex: 1; min-height: 0;` : `flex: 0 0 auto;`}
`;
